import { type Href, router } from 'expo-router';
import { ArrowLeftIcon, ChevronLeftIcon, XIcon } from 'lucide-react-native';
import { Stack, type StackProps } from 'tamagui';

import { hitSlop } from '../componentHelpers';
import { Icon, type IconType } from '../icons/Icon';

export enum BackButtonIconType {
    LEFT = 'left',
    X = 'x',
    ARROW_LEFT = 'arrowLeft',
}

const BackButtonIcon: Record<BackButtonIconType, IconType> = {
    [BackButtonIconType.LEFT]: ChevronLeftIcon,
    [BackButtonIconType.X]: XIcon,
    [BackButtonIconType.ARROW_LEFT]: ArrowLeftIcon,
};

interface BackButtonProps extends Omit<StackProps, 'onPress'> {
    background?: 'transparent' | 'gray' | 'white';
    fallbackRoute?: Href;
    iconType?: BackButtonIconType;
    customBackFn?: () => void;
    onBeforeGoingBack?: () => void;
}

export const BackButton = ({
    background = 'gray',
    fallbackRoute = '/',
    iconType = BackButtonIconType.LEFT,
    customBackFn,
    onBeforeGoingBack,
    ...props
}: BackButtonProps) => {
    const buttonPadding =
        background === 'gray' || background === 'transparent'
            ? 8
            : iconType === BackButtonIconType.ARROW_LEFT
              ? 3.5
              : 9;

    const backgroundColor =
        background === 'white'
            ? '$white'
            : background === 'transparent'
              ? 'transparent'
              : '$gray200';

    return (
        <Stack
            testID="back-button"
            {...hitSlop(16)}
            flexDirection="row"
            alignSelf="flex-start"
            justifyContent="center"
            alignItems="center"
            backgroundColor={backgroundColor}
            padding={buttonPadding}
            borderRadius={16}
            onPress={() => {
                if (customBackFn) {
                    customBackFn();
                } else {
                    onBeforeGoingBack?.();
                    if (router.canGoBack()) {
                        router.back();
                    } else {
                        router.replace(fallbackRoute);
                    }
                }
            }}
            animation="button"
            animateOnly={['transform', 'backgroundColor']}
            pressStyle={{
                scale: 0.95,
            }}
            cursor="pointer"
            {...props}
        >
            <Stack justifyContent="center" alignItems="center">
                <Icon
                    icon={BackButtonIcon[iconType]}
                    color="$black"
                    size={iconType === BackButtonIconType.ARROW_LEFT ? 24 : 16}
                />
            </Stack>
        </Stack>
    );
};
